/**
 * Session utilisateur : jetons Cognito, restauration au chargement, rafraîchissement automatique (2 min avant
 * expiration), déconnexion.
 * Stockage : jetons d'identité / d'accès en mémoire + sessionStorage (onglet) ; refresh token en localStorage
 * (valable quelques heures, révoqué à la déconnexion) pour ne pas redemander la connexion à chaque onglet.
 * L'API n'accepte que le JETON D'ACCÈS (jeton délégué, client émetteur vérifié) : l'id_token sert seulement à
 * l'affichage immédiat du profil (lireProfil) et n'est jamais envoyé à l'API.
 * Seul un refresh token refusé par Cognito (expiré, révoqué) ferme la session. Un échec passager du rafraîchissement
 * (réseau coupé, Cognito indisponible : estTransitoire) la garde ouverte et réessaie, pour ne pas couper un appel ou
 * un enregistrement en cours : le jeton d'accès reste utilisé jusqu'à son expiration, puis jetonValide rejette
 * RenouvellementReporte (erreur réseau pour le client API) jusqu'au prochain rafraîchissement réussi.
 */
import React from 'react';
import type { ConfigApp } from '../config';
import { deconnexionDistante, estTransitoire, rafraichirJetons } from './cognito';

export type OrigineJetons = 'code' | 'sso' | 'maquette';

export interface Jetons {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expireA: number;         // ms epoch
  origine: OrigineJetons;
}

/** Contenu utile du jeton d'identité (affichage immédiat ; la référence reste GET /v1/moi). */
export interface ProfilJeton { sub: string; email: string; prenom: string; nom: string; groupes: string[] }

const CLE_JETONS = 'agora.jetons';
const CLE_REFRESH = 'agora.refresh';
const MARGE_MS = 2 * 60 * 1000;
/** Nouvel essai après un échec passager du rafraîchissement : délai croissant, puis une fois par minute. */
export const DELAIS_ESSAI_MS = [5_000, 10_000, 20_000, 40_000, 60_000];

/** Rafraîchissement reporté (échec passager) : la session reste ouverte, mais aucun jeton d'accès valide pour l'instant. */
export class RenouvellementReporte extends Error {
  constructor() { super('renouvellement-reporte'); }
}

export function lireProfil(idToken: string): ProfilJeton | null {
  try {
    const charge = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(decodeURIComponent(escape(atob(charge)))) as Record<string, unknown>;
    return {
      sub: String(json.sub || ''), email: String(json.email || ''), prenom: String(json.given_name || ''), nom: String(json.family_name || ''),
      groupes: Array.isArray(json['cognito:groups']) ? (json['cognito:groups'] as string[]) : [],
    };
  } catch {
    return null;
  }
}

function lireStockage(): Jetons | null {
  try {
    const j = JSON.parse(sessionStorage.getItem(CLE_JETONS) || 'null') as Jetons | null;
    const r = JSON.parse(localStorage.getItem(CLE_REFRESH) || 'null') as { refreshToken: string; origine: OrigineJetons } | null;
    if (j) return { ...j, refreshToken: r?.refreshToken };
    if (r) return { idToken: '', accessToken: '', expireA: 0, refreshToken: r.refreshToken, origine: r.origine };
  } catch { /* stockage indisponible */ }
  return null;
}

function ecrireStockage(j: Jetons | null) {
  try {
    if (!j) {
      sessionStorage.removeItem(CLE_JETONS);
      localStorage.removeItem(CLE_REFRESH);
      return;
    }
    const { refreshToken, ...reste } = j;
    sessionStorage.setItem(CLE_JETONS, JSON.stringify(reste));
    if (refreshToken) localStorage.setItem(CLE_REFRESH, JSON.stringify({ refreshToken, origine: j.origine }));
  } catch { /* stockage indisponible */ }
}

export type EtatSession = 'chargement' | 'connecte' | 'deconnecte';

interface ContexteSession {
  etat: EtatSession;
  jetons: Jetons | null;
  profil: ProfilJeton | null;
  /**
   * Jeton d'ACCÈS valide pour l'API (rafraîchi si besoin) ; null si la session est perdue ; rejette
   * RenouvellementReporte si le rafraîchissement a échoué pour une raison passagère et que le jeton a expiré.
   */
  jetonValide: (forcer?: boolean) => Promise<string | null>;
  ouvrir: (j: Jetons) => void;
  fermer: (opts?: { distante?: boolean }) => Promise<void>;
}

const Ctx = React.createContext<ContexteSession | null>(null);

export function SessionProvider({ config, children }: { config: ConfigApp; children: React.ReactNode }) {
  const [jetons, setJetons] = React.useState<Jetons | null>(null);
  const [etat, setEtat] = React.useState<EtatSession>('chargement');
  const ref = React.useRef<Jetons | null>(null);
  const enCours = React.useRef<Promise<string | null> | null>(null);
  const etatRef = React.useRef(etat);
  etatRef.current = etat;
  // Échecs passagers du rafraîchissement depuis le dernier succès (0 = aucun nouvel essai prévu).
  const [echecs, setEchecs] = React.useState(0);

  const appliquer = React.useCallback((j: Jetons | null) => {
    ref.current = j;
    ecrireStockage(j);
    setJetons(j);
    setEtat(j ? 'connecte' : 'deconnecte');
    setEchecs(0);
  }, []);

  const rafraichir = React.useCallback(async (): Promise<string | null> => {
    const j = ref.current;
    if (!j?.refreshToken || j.origine === 'maquette') return j?.origine === 'maquette' ? j.accessToken : null;
    if (!enCours.current) {
      enCours.current = rafraichirJetons(config, j.refreshToken, j.origine)
        .then((n) => { appliquer(n); return n.accessToken; })
        .catch((e: unknown) => {
          if (ref.current !== j) return null;   // déconnexion pendant l'essai
          if (!estTransitoire(e)) { appliquer(null); return null; }
          // Session ouverte : nouvel essai planifié. Au chargement de la page : écran de connexion, mais le refresh
          // token reste enregistré (un rechargement réessaie).
          if (etatRef.current === 'connecte') setEchecs((n) => n + 1);
          else setEtat('deconnecte');
          throw new RenouvellementReporte();
        })
        .finally(() => { enCours.current = null; });
    }
    return enCours.current;
  }, [appliquer, config]);

  const jetonValide = React.useCallback(async (forcer = false) => {
    const j = ref.current;
    if (!j) return null;
    if (j.origine === 'maquette') return j.accessToken;
    if (!forcer && j.accessToken && j.expireA - Date.now() > MARGE_MS) return j.accessToken;
    try {
      return await rafraichir();
    } catch (e) {
      // Jeton d'accès encore valable (dans la marge de 2 min) : on s'en sert en attendant le prochain essai.
      if (!forcer && j.accessToken && j.expireA > Date.now()) return j.accessToken;
      throw e;
    }
  }, [rafraichir]);

  // Restauration au chargement.
  React.useEffect(() => {
    const j = lireStockage();
    if (!j) { setEtat('deconnecte'); return; }
    ref.current = j;
    if (j.origine === 'maquette' || (j.accessToken && j.expireA - Date.now() > MARGE_MS)) appliquer(j);
    else void rafraichir().catch(() => undefined);
  }, [appliquer, rafraichir]);

  // Échec passager : nouvel essai avec un délai croissant, et tout de suite au retour du réseau ou de l'onglet.
  React.useEffect(() => {
    if (!echecs) return;
    const essayer = () => { void rafraichir().catch(() => undefined); };
    const visible = () => { if (document.visibilityState === 'visible') essayer(); };
    const t = window.setTimeout(essayer, DELAIS_ESSAI_MS[Math.min(echecs, DELAIS_ESSAI_MS.length) - 1]);
    window.addEventListener('online', essayer);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('online', essayer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [echecs, rafraichir]);

  // Rafraîchissement planifié 2 min avant l'expiration.
  React.useEffect(() => {
    if (!jetons || jetons.origine === 'maquette') return;
    const delai = Math.max(5_000, jetons.expireA - Date.now() - MARGE_MS);
    const t = window.setTimeout(() => void rafraichir().catch(() => undefined), delai);
    return () => window.clearTimeout(t);
  }, [jetons, rafraichir]);

  const fermer = React.useCallback(async (opts?: { distante?: boolean }) => {
    const j = ref.current;
    appliquer(null);
    if (opts?.distante !== false && j && j.origine !== 'maquette' && !config.maquette) await deconnexionDistante(config, j);
  }, [appliquer, config]);

  const valeur = React.useMemo<ContexteSession>(() => ({
    etat, jetons, profil: jetons?.idToken ? lireProfil(jetons.idToken) : null, jetonValide, ouvrir: appliquer, fermer,
  }), [etat, jetons, jetonValide, appliquer, fermer]);

  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export function useSession(): ContexteSession {
  const c = React.useContext(Ctx);
  if (!c) throw new Error('useSession hors de SessionProvider');
  return c;
}
